import React, { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

export default 
function CreatePostModal({ user, onClose, onSuccess }) {
  const [caption, setCaption] = useState("");
  const [articleUrl, setArticleUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageOk, setImageOk] = useState(null); // null | true | false
  const [schedMode, setSchedMode] = useState("now"); // "now" | "later"
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const [schedDate, setSchedDate] = useState(tomorrow.toISOString().slice(0, 10));
  const [schedHour, setSchedHour] = useState(9);
  const [schedMin, setSchedMin] = useState(0);
  const [rewriting, setRewriting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [rewritesLeft, setRewritesLeft] = useState(null);
  const [captionFlash, setCaptionFlash] = useState(false);
  const [taRef, setTaRef] = useState(null);

  const plan = (user?.plan || "free").toLowerCase();
  const isBasic = plan === "free";
  const todayStr = new Date().toISOString().slice(0, 10);
  const userTz = user?.review_timezone || user?.timezone || "your timezone";

  // Auto-resize textarea
  useEffect(() => {
    if (!taRef) return;
    taRef.style.height = "auto";
    taRef.style.height = Math.max(140, taRef.scrollHeight) + "px";
  }, [caption, taRef]);

  const handleRewrite = async () => {
    if (!caption.trim() || rewriting) return;
    setRewriting(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/rewrite-caption`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caption, tone: user?.tone }),
      });
      const d = await r.json();
      if (r.ok) {
        setCaption(d.rewritten_caption);
        setRewritesLeft(d.rewrites_remaining);
        setCaptionFlash(true);
        setTimeout(() => setCaptionFlash(false), 800);
      } else if (r.status === 429) {
        setRewritesLeft(0);
      }
    } catch { /* ignore */ }
    setRewriting(false);
  };

  const handleSubmit = async () => {
    setSubmitError("");
    if (!caption.trim()) { setSubmitError("Caption is required."); return; }
    setSubmitting(true);

    let scheduled_for = null;
    if (schedMode === "later") {
      const dt = new Date(`${schedDate}T${String(schedHour).padStart(2, "0")}:${String(schedMin).padStart(2, "0")}:00`);
      if (isNaN(dt.getTime())) { setSubmitError("Invalid date/time."); setSubmitting(false); return; }
      scheduled_for = dt.toISOString();
    }

    try {
      const r = await fetch(`${API_BASE}/app/api/user/create-custom-post`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caption,
          article_url: articleUrl || null,
          image_url: imageUrl || null,
          scheduled_for,
          publish_now: schedMode === "now",
        }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        if (schedMode === "now") {
          onSuccess("Your post is live on LinkedIn! 🎉");
        } else {
          const sdt = new Date(d.scheduled_for);
          const fmt = sdt.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
          onSuccess(`Scheduled for ${fmt} ✓`);
        }
      } else if (r.status === 422 && d.error === "duplicate_content") {
        setSubmitError("LinkedIn rejected this — try rewording your post slightly");
      } else if (r.status === 403 && d.error === "upgrade_required") {
        setSubmitError("Upgrade to Presence to schedule posts");
      } else {
        setSubmitError(d.message || d.error || "Something went wrong. Please try again.");
      }
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const planRewriteLimit = plan === "authority" || plan === "authority_pro" || plan === "team" || plan === "agency" || plan === "authority_team" || plan === "authority_team_member" || plan === "pro_team" || plan === "pro_team_member" ? 25 : plan === "free" ? 0 : 10;
  const rewriteLabel = rewritesLeft === null ? `${planRewriteLimit} rewrites/day`
    : rewritesLeft <= 0 ? "Resets tomorrow"
    : `${rewritesLeft} rewrite${rewritesLeft !== 1 ? "s" : ""} left today`;

  const INPUT = { width: "100%", border: "1px solid #E1E9EE", borderRadius: 8, padding: "10px 14px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box", color: "#1A1A1A", background: "#fff" };
  const LABEL = { fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, color: "#333", display: "block", marginBottom: 6 };
  const HELPER = { fontSize: 12, color: "#999", marginTop: 4, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 580, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.22)" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #E1E9EE", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: "#1A1A1A" }}>Create a LinkedIn Post</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#999", padding: 4, lineHeight: 1, borderRadius: 4 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>

          {/* Caption */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              background: `linear-gradient(to bottom, rgba(0,119,181,0.06) 0px, rgba(0,119,181,0.06) 78px, #fff 78px)`,
              border: `1.5px solid ${captionFlash ? "#057642" : "#E1E9EE"}`,
              borderRadius: 8, transition: "border-color 0.4s",
            }}>
              <textarea
                ref={el => setTaRef(el)}
                value={caption}
                onChange={e => setCaption(e.target.value.slice(0, 3000))}
                disabled={rewriting}
                placeholder="Write your LinkedIn post..."
                style={{ width: "100%", border: "none", outline: "none", background: "transparent", padding: "12px 14px 4px", resize: "none", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, lineHeight: "22px", color: "#1A1A1A", minHeight: 140, boxSizing: "border-box", opacity: rewriting ? 0.5 : 1, transition: "opacity 0.2s", display: "block" }}
              />
              <div style={{ textAlign: "right", padding: "2px 12px 8px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: caption.length > 2700 ? "#C0392B" : "#999" }}>
                {caption.length} / 3000
              </div>
            </div>
            {caption.length > 210 && (
              <div style={{ ...HELPER, color: "#0077B5", marginTop: 5 }}>↑ Blue shaded area ≈ what LinkedIn shows before "see more"</div>
            )}
          </div>

          {/* Article URL */}
          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Article URL <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span></label>
            <input value={articleUrl} onChange={e => setArticleUrl(e.target.value)} placeholder="https://..." style={INPUT} />
            <div style={HELPER}>The link will be added as a comment to protect your post's reach on LinkedIn</div>
          </div>

          {/* Image URL */}
          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Image URL <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span></label>
            <input value={imageUrl} onChange={e => { setImageUrl(e.target.value); setImageOk(null); }} placeholder="https://...jpg or .png or .gif" style={INPUT} />
            <div style={HELPER}>PNG, JPG, or GIF only. Max 5MB.</div>
            {imageUrl && /\.(png|jpg|jpeg|gif)$/i.test(imageUrl) && (
              <div style={{ marginTop: 8 }}>
                <img src={imageUrl} alt="Preview" onLoad={() => setImageOk(true)} onError={() => setImageOk(false)}
                  style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8, border: "1px solid #E1E9EE", display: imageOk === false ? "none" : "block" }} />
                {imageOk === false && <div style={{ ...HELPER, color: "#C0392B" }}>Could not preview image — check the URL</div>}
              </div>
            )}
          </div>

          {/* AI Rewrite (non-basic only) */}
          {!isBasic && (
            <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button
                onClick={handleRewrite}
                disabled={rewriting || rewritesLeft === 0 || !caption.trim()}
                style={{ background: "transparent", border: "1.5px solid #0077B5", borderRadius: 20, padding: "8px 16px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, cursor: (rewriting || rewritesLeft === 0 || !caption.trim()) ? "not-allowed" : "pointer", color: "#0077B5", display: "flex", alignItems: "center", gap: 6, opacity: (rewritesLeft === 0 || !caption.trim()) ? 0.45 : 1, whiteSpace: "nowrap" }}
              >
                {rewriting ? <><span style={{ display: "inline-block", animation: "composerSpin 0.8s linear infinite" }}>⟳</span> Rewriting…</> : "✨ Rewrite with AI"}
              </button>
              <div style={{ ...HELPER, marginTop: 0, color: rewritesLeft === 0 ? "#C0392B" : "#999" }}>{rewriteLabel}</div>
            </div>
          )}

          {/* Scheduling toggle */}
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {[["now", "Post Now"], ["later", "Schedule for later"]].map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => { if (!isBasic || m === "now") setSchedMode(m); }}
                  title={isBasic && m === "later" ? "Upgrade to Presence to schedule posts" : undefined}
                  style={{ padding: "8px 18px", border: `1.5px solid ${schedMode === m ? "#0077B5" : "#E1E9EE"}`, borderRadius: 20, background: schedMode === m ? "#EBF5FB" : "#fff", color: schedMode === m ? "#0077B5" : (isBasic && m === "later" ? "#bbb" : "#666"), cursor: isBasic && m === "later" ? "not-allowed" : "pointer", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, fontWeight: schedMode === m ? 600 : 400, opacity: isBasic && m === "later" ? 0.5 : 1 }}
                >
                  {label}
                </button>
              ))}
            </div>

            {schedMode === "later" && (
              <div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="date" value={schedDate} min={todayStr} onChange={e => setSchedDate(e.target.value)}
                    style={{ border: "1px solid #E1E9EE", borderRadius: 8, padding: "8px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none" }} />
                  <select value={schedHour} onChange={e => setSchedHour(Number(e.target.value))}
                    style={{ border: "1px solid #E1E9EE", borderRadius: 8, padding: "8px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none" }}>
                    {Array.from({ length: 24 }, (_, i) => {
                      const h = i === 0 ? 12 : i > 12 ? i - 12 : i;
                      return <option key={i} value={i}>{String(h).padStart(2, "0")} {i < 12 ? "AM" : "PM"}</option>;
                    })}
                  </select>
                  <select value={schedMin} onChange={e => setSchedMin(Number(e.target.value))}
                    style={{ border: "1px solid #E1E9EE", borderRadius: 8, padding: "8px 12px", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 14, outline: "none" }}>
                    {[0, 15, 30, 45].map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
                  </select>
                </div>
                <div style={{ ...HELPER, marginTop: 6 }}>Times shown in {userTz}</div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #E1E9EE", flexShrink: 0 }}>
          {submitError && (
            <div style={{ color: "#C0392B", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, marginBottom: 12, padding: "8px 12px", background: "#FEF2F2", borderRadius: 6, border: "1px solid #FECDD3" }}>{submitError}</div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#666", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 15, padding: "8px 0" }}>Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !caption.trim()}
              style={{ background: (!caption.trim() || submitting) ? "#EBF5FB" : "#0077B5", color: (!caption.trim() || submitting) ? "#999" : "#fff", border: "none", borderRadius: 10, padding: "12px 28px", fontFamily: "'Playfair Display',Georgia,serif", fontSize: 16, cursor: (!caption.trim() || submitting) ? "not-allowed" : "pointer", minWidth: 160, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {submitting
                ? <><span style={{ display: "inline-block", animation: "composerSpin 0.8s linear infinite" }}>⟳</span> Posting…</>
                : schedMode === "now" ? "Post Now →" : "Schedule Post →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Busy Bee banner ──────────────────────────────────────────────────────────
